import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  ChevronDown, Building2, LayoutList, Code2, Boxes, Layers,
  Lightbulb, Heart, ClipboardCheck, AlertTriangle, CheckSquare,
  Brain, Target, Wrench, MessageSquare, Clock, HelpCircle
} from "lucide-react";

/* =================== NAV CONFIG =================== */
const NAV_ITEMS = [
  { id: "about-emergent", label: "About" },
  { id: "interview-format", label: "Format" },
  { id: "machine-coding", label: "Machine Coding" },
  { id: "hld-round", label: "HLD" },
  { id: "lld-round", label: "LLD" },
  { id: "general-tips", label: "Tips" },
  { id: "what-we-value", label: "Values" },
  { id: "day-of-checklist", label: "Checklist" }
];

/* =================== STICKY NAV =================== */
function StickyNav({ activeId }) {
  const navRef = useRef(null);

  useEffect(() => {
    if (!navRef.current) return;
    const activeLink = navRef.current.querySelector(`[data-section="${activeId}"]`);
    if (activeLink) {
      activeLink.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [activeId]);

  return (
    <nav
      data-testid="sticky-nav"
      className="sticky top-0 z-50 border-b bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/80"
      style={{ borderColor: "hsl(0 0% 90%)" }}
      aria-label="Section navigation"
    >
      <div className="mx-auto w-full max-w-[72rem] px-4 sm:px-6 lg:px-8">
        <div
          ref={navRef}
          className="flex w-full items-center gap-2 overflow-x-auto no-scrollbar py-3"
          style={{ scrollSnapType: "x mandatory" }}
        >
          {NAV_ITEMS.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              data-section={item.id}
              data-testid={`sticky-nav-link-${item.id}`}
              className={`nav-link shrink-0 rounded-full border px-3 py-1.5 text-xs sm:text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                activeId === item.id
                  ? "active"
                  : "border-border bg-white text-foreground/70 hover:text-foreground hover:border-foreground/20"
              }`}
              style={{ scrollSnapAlign: "center" }}
            >
              {item.label}
            </a>
          ))}
        </div>
      </div>
    </nav>
  );
}

/* =================== HERO =================== */
function HeroSection() {
  return (
    <section className="relative">
      <div className="mx-auto w-full max-w-[72rem] px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 lg:pt-24 pb-12 sm:pb-16 text-center">
        <div className="fade-in-up" style={{ animationDelay: "0.1s" }}>
          <p
            className="text-[11px] sm:text-xs tracking-[0.1em] uppercase mb-4"
            style={{ fontFamily: "var(--font-mono)", color: "rgb(128, 128, 128)" }}
          >
            Emergent
          </p>
        </div>
        <h1
          className="fade-in-up text-3xl sm:text-5xl lg:text-[3.5rem] font-semibold tracking-tight mx-auto"
          style={{ fontFamily: "var(--font-heading)", animationDelay: "0.2s", letterSpacing: "-0.03em" }}
        >
          <span style={{ color: "rgb(5, 95, 250)" }}>Candidate Interview</span>
          <br />
          Prep Guide
        </h1>
        <p className="fade-in-up mt-5 max-w-[52ch] mx-auto text-sm sm:text-base leading-7" style={{ animationDelay: "0.3s", color: "rgb(69, 69, 84)" }}>
          This guide helps you prepare for your technical interview at Emergent.
          Read it thoroughly &mdash; the better you understand our process, the better you'll perform.
        </p>
        <div className="fade-in-up mt-7" style={{ animationDelay: "0.4s" }}>
          <a
            href="#about-emergent"
            data-testid="hero-cta"
            className="inline-flex items-center gap-2 text-sm font-medium text-white px-6 py-2.5 rounded-full"
            style={{ backgroundColor: "rgb(5, 95, 250)", transition: "opacity 0.15s ease", letterSpacing: "-0.02em" }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = "0.9"}
            onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
          >
            Start reading
            <ChevronDown className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

/* =================== SECTION WRAPPER =================== */
function SectionWrapper({ id, icon: Icon, number, title, badge, children }) {
  return (
    <div id={id} data-testid={`guide-section-${id}`} className="scroll-mt-24">
      <Card className="section-card rounded-xl border border-border bg-white shadow-none">
        <div className="p-5 sm:p-7 lg:p-8">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-9 h-9 rounded-lg" style={{ backgroundColor: "hsl(0 0% 96%)" }}>
                <Icon className="w-[18px] h-[18px]" style={{ color: "rgb(128, 128, 128)" }} />
              </div>
              <span className="text-xs" style={{ fontFamily: "var(--font-mono)", color: "rgb(128, 128, 128)", letterSpacing: "0.04em" }}>
                Section {number}
              </span>
            </div>
            {badge && (
              <Badge variant="secondary" className="text-[11px] tracking-tight font-medium border-0" style={{ fontFamily: "var(--font-mono)", backgroundColor: "hsl(0 0% 96%)", color: "rgb(69, 69, 84)" }}>
                {badge}
              </Badge>
            )}
          </div>
          <h2
            data-testid={`guide-section-title-${id}`}
            className="text-xl sm:text-2xl font-semibold tracking-tight mb-4"
            style={{ fontFamily: "var(--font-heading)", letterSpacing: "-0.02em", color: "rgb(0, 0, 0)" }}
          >
            {title}
          </h2>
          {children}
        </div>
      </Card>
    </div>
  );
}

/* =================== ABOUT EMERGENT =================== */
function AboutSection() {
  return (
    <SectionWrapper id="about-emergent" icon={Building2} number="01" title="About Emergent">
      <p className="text-sm sm:text-base leading-7 text-muted-foreground max-w-[68ch]">
        <a href="https://emergent.sh" target="_blank" rel="noopener noreferrer" className="font-medium text-foreground underline underline-offset-4 decoration-border hover:decoration-foreground">Emergent</a> is
        an AI agent platform where users describe apps in natural language and autonomous AI agents build them &mdash; writing code, debugging, running tests, and deploying.
        Emergent is not a code editor, IDE, or copilot. <span className="font-medium text-foreground">The agent is the developer.</span>
      </p>
      <p className="text-sm sm:text-base leading-7 text-muted-foreground mt-3 max-w-[68ch]">
        This means the engineering team builds the infrastructure that makes autonomous coding agents reliable, fast, and scalable.
      </p>
      <div className="mt-5 rounded-lg border px-4 py-3" style={{ borderColor: "hsl(0 0% 90%)", backgroundColor: "hsl(0 0% 97%)" }}>
        <p className="text-sm leading-6" style={{ color: "rgb(69, 69, 84)" }}>
          <span className="font-semibold" style={{ color: "rgb(0, 0, 0)" }}>Why this matters for your interview:</span> Our problems are inspired by real challenges we face &mdash; routing LLM requests under constraints, orchestrating autonomous agents, managing infrastructure at scale, and building systems where the "user" is an unpredictable AI making decisions in real time. Expect problems that test <span className="font-medium">depth, not breadth</span>.
        </p>
      </div>
    </SectionWrapper>
  );
}

/* =================== INTERVIEW FORMAT =================== */
function FormatSection() {
  return (
    <SectionWrapper id="interview-format" icon={LayoutList} number="02" title="Interview Format">
      <p className="text-sm sm:text-base leading-7 text-muted-foreground mb-5 max-w-[68ch]">
        Your interview will consist of one or more of the following round types, depending on the role and level:
      </p>

      <div className="table-wrapper mb-6">
        <table className="format-table">
          <thead>
            <tr>
              <th>Round</th>
              <th>Duration</th>
              <th>What It Tests</th>
              <th>Deliverable</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Machine Coding (MC)</td>
              <td style={{ fontFamily: "var(--font-mono)", fontSize: "0.8125rem" }}>60 &ndash; 75 min</td>
              <td>Working code under constraints, concurrency, failure handling, design quality</td>
              <td>Running code in your language of choice</td>
            </tr>
            <tr>
              <td>High Level Design (HLD)</td>
              <td style={{ fontFamily: "var(--font-mono)", fontSize: "0.8125rem" }}>55 &ndash; 65 min</td>
              <td>System architecture, component design, failure domains, scale reasoning, operational maturity</td>
              <td>Architecture diagram (mandatory) + verbal walkthrough</td>
            </tr>
            <tr>
              <td>Low Level Design (LLD)</td>
              <td style={{ fontFamily: "var(--font-mono)", fontSize: "0.8125rem" }}>55 &ndash; 65 min</td>
              <td>Interface contracts, data models, concurrency semantics, storage design, migration safety</td>
              <td>Concrete schemas, API signatures, and sequence diagrams</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="subsection-title">Progressive Disclosure Format</p>
      <p className="text-sm sm:text-base leading-7 text-muted-foreground mb-3 max-w-[68ch]">
        All rounds follow a progressive disclosure format:
      </p>
      <ul className="guide-list">
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Phase 1:</span> Core requirements given upfront. Solve these first.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Phase 2:</span> Additional requirements revealed after Phase 1 is solid. These add complexity.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Phase 3:</span> Verbal follow-ups that probe deeper &mdash; scale, failure, tradeoffs.</li>
      </ul>
    </SectionWrapper>
  );
}

/* =================== MACHINE CODING =================== */
function MachineCodingSection() {
  return (
    <SectionWrapper id="machine-coding" icon={Code2} number="03" title="Machine Coding Round" badge="60 - 75 min">
      <p className="text-sm sm:text-base leading-7 text-muted-foreground mb-5 max-w-[68ch]">
        You'll be given a backend system problem with clear inputs, outputs, and constraints. You'll write working code in the language of your choice (Python, Go, Java, TypeScript are common). The problem starts simple and gets progressively harder.
      </p>

      <p className="subsection-title">Setup Requirements</p>
      <ul className="guide-list">
        <li className="text-sm sm:text-base text-foreground/90">Your preferred programming language and IDE should be fully installed and configured.</li>
        <li className="text-sm sm:text-base text-foreground/90">You should be able to write, run, and debug code locally on your laptop using your IDE.</li>
        <li className="text-sm sm:text-base text-foreground/90">You can use any AI coding assistant (e.g., GitHub Copilot, Cursor, Claude Code, etc.) &mdash; please ensure it is installed, configured, and working before the interview.</li>
      </ul>

      <p className="subsection-title">Core Concepts to Know</p>
      <ul className="guide-list">
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Concurrency primitives:</span> Mutexes, semaphores, atomic operations, CAS (compare-and-swap). Know when to use each.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">State machines:</span> Many problems involve components that transition between states (healthy/degraded/unhealthy, open/closed/half-open). Practice implementing clean state machines.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Rate limiting:</span> Sliding window, token bucket, leaky bucket. Understand the tradeoffs between them.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Retry and fallback patterns:</span> Exponential backoff, fallback chains, circuit breakers. Know how to implement them, not just name them.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Data structures for time-series:</span> Sliding windows, ring buffers, EWMA (exponentially weighted moving averages). These appear frequently.</li>
      </ul>

      <p className="subsection-title">What We Evaluate</p>
      <div className="table-wrapper mb-5">
        <table className="eval-table">
          <thead>
            <tr>
              <th>Criteria</th>
              <th>What "Good" Looks Like</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Correctness</td>
              <td>Your code handles the happy path AND edge cases &mdash; empty inputs, concurrent access, timeout, failure mid-operation</td>
            </tr>
            <tr>
              <td>Concurrency</td>
              <td>You proactively identify race conditions and handle them. Not just "I should add a lock" but "here's the specific race between check and act"</td>
            </tr>
            <tr>
              <td>Design Quality</td>
              <td>Clean separation of concerns. The rate limiter, health tracker, and selector are distinct modules &mdash; not one giant function</td>
            </tr>
            <tr>
              <td>Failure Handling</td>
              <td>Graceful degradation. When things go wrong (and they will in Phase 2), your system doesn't crash &mdash; it falls back, retries, or returns a meaningful error</td>
            </tr>
            <tr>
              <td>Communication</td>
              <td>You think aloud. You explain why before you write what. You discuss tradeoffs without being asked</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="subsection-title">Common Mistakes to Avoid</p>
      <ul className="guide-list">
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Jumping into code without a plan.</span> Spend 3-5 minutes sketching your approach. The interviewer values your thinking process.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Ignoring concurrency.</span> If the problem says "concurrent requests," assume multiple threads are calling your code simultaneously. Think about what shared state exists and how you protect it.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Forgetting resource cleanup.</span> If you reserve/deduct something (tokens, slots, budget), make sure you release it on failure and timeout &mdash; not just on success.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Hardcoding values.</span> Use configurable thresholds, not magic numbers.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Not testing mentally.</span> After writing code, trace through a scenario: "If request A arrives, then B arrives concurrently, what happens?"</li>
      </ul>
    </SectionWrapper>
  );
}

/* =================== HLD =================== */
function HLDSection() {
  return (
    <SectionWrapper id="hld-round" icon={Boxes} number="04" title="High Level Design (HLD) Round" badge="55 - 65 min">
      <p className="text-sm sm:text-base leading-7 text-muted-foreground mb-3 max-w-[68ch]">
        You'll be asked to design a complete system &mdash; the kind that spans multiple services, databases, and deployment boundaries. Problems are inspired by real Emergent infrastructure challenges: agent orchestration, deployment pipelines, experimentation frameworks, and more.
      </p>

      <div className="alert-box">
        <p>
          <span className="font-semibold">Architecture Diagram is MANDATORY.</span> You must produce a structured architecture diagram (whiteboard-style with boxes, arrows, and labels) BEFORE deep-diving into any component. The interviewer will explicitly ask for it. The diagram anchors the entire discussion.
        </p>
      </div>

      <p className="subsection-title">Core Concepts to Know</p>
      <ul className="guide-list">
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Microservice decomposition:</span> When to split services, how they communicate (sync vs async), failure isolation.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Message queues and event buses:</span> Kafka, RabbitMQ, SQS. Know when to use each and the ordering/delivery guarantees.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Database selection:</span> When to use relational (PostgreSQL) vs document (MongoDB) vs graph vs key-value (Redis). Don't just pick one &mdash; explain why.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Container orchestration:</span> Kubernetes basics &mdash; pods, services, deployments, HPA, resource limits.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Failure domains and blast radius:</span> What happens when component X dies? Does it take down the whole system or just one feature?</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Deployment strategies:</span> Blue-green, canary, progressive rollout. Know the tradeoffs.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Observability:</span> Structured logging, distributed tracing (correlation IDs), metrics, and alerting. Not an afterthought &mdash; a first-class concern.</li>
      </ul>

      <p className="subsection-title">Architecture Diagram Rubric</p>
      <div className="table-wrapper mb-5">
        <table className="rubric-table">
          <thead>
            <tr>
              <th>Level</th>
              <th>What the Diagram Shows</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ color: "hsl(0 60% 50%)" }}>Weak (1)</td>
              <td>No diagram, or just a list of component names with no connections</td>
            </tr>
            <tr>
              <td style={{ color: "hsl(35 80% 45%)" }}>Adequate (2)</td>
              <td>Major components as boxes, but missing connection labels, data flow direction, or protocol annotations</td>
            </tr>
            <tr>
              <td style={{ color: "hsl(188 55% 42%)" }}>Strong (3)</td>
              <td>Clear component boxes with labeled connections, data flow arrows with protocol/transport annotations (HTTP, gRPC, Kafka), logical groupings visible</td>
            </tr>
            <tr>
              <td style={{ color: "hsl(150 60% 35%)" }}>Exceptional (4)</td>
              <td>All of level 3 plus: failure domains marked, async vs sync paths distinguished, scale boundaries identified, control plane vs data plane separated. The diagram tells the story without verbal explanation</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="subsection-title">Tips for a Great Diagram</p>
      <ul className="guide-list">
        <li className="text-sm sm:text-base text-foreground/90">Draw it FIRST, before going deep on any component.</li>
        <li className="text-sm sm:text-base text-foreground/90">Label every arrow with the protocol (HTTP, gRPC, Kafka, Redis pub/sub).</li>
        <li className="text-sm sm:text-base text-foreground/90">Distinguish sync (solid arrow) from async (dashed arrow) paths.</li>
        <li className="text-sm sm:text-base text-foreground/90">Group related components into logical planes or domains.</li>
        <li className="text-sm sm:text-base text-foreground/90">Mark where failures would be isolated (failure domains).</li>
        <li className="text-sm sm:text-base text-foreground/90">Identify the bottleneck &mdash; which component becomes the constraint at scale?</li>
      </ul>

      <p className="subsection-title">What We Evaluate</p>
      <div className="table-wrapper mb-5">
        <table className="eval-table">
          <thead>
            <tr>
              <th>Criteria</th>
              <th>What "Good" Looks Like</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Architecture Diagram</td>
              <td>Clear, complete, labeled. Someone could understand the system from the diagram alone</td>
            </tr>
            <tr>
              <td>Component Design</td>
              <td>Clear responsibilities for each service. No "god service" that does everything. Clean API boundaries</td>
            </tr>
            <tr>
              <td>Failure Handling</td>
              <td>You discuss what happens when each major component fails. You scope blast radius. You have recovery playbooks, not just "restart it"</td>
            </tr>
            <tr>
              <td>Scale Reasoning</td>
              <td>You identify bottlenecks and explain how to relieve them. You discuss concrete numbers (QPS, latency targets, storage growth)</td>
            </tr>
            <tr>
              <td>Operational Maturity</td>
              <td>You discuss observability, deployment strategy, SLOs, on-call concerns &mdash; not as an afterthought, but as part of the design</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="subsection-title">Common Mistakes to Avoid</p>
      <ul className="guide-list">
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Skipping the diagram.</span> The interviewer will ask for it. Don't start with a verbal description &mdash; draw first, talk second.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">"I'll use Kafka for everything."</span> Every technology choice should have a reason. Why Kafka and not SQS? Why gRPC and not REST?</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Ignoring constraints.</span> If the problem says "single container" or "must handle 1M events/sec," design around the constraint.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">No failure discussion.</span> If you only describe the happy path, you'll score low. The interviewer will ask "what happens when X dies?" &mdash; be ready.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Vague SLOs.</span> Don't say "it should be fast." Say "control plane reads should be p99 &lt; 300ms, writes p99 &lt; 1s."</li>
      </ul>

      <p className="subsection-title">Practice Strategy</p>
      <ul className="guide-list">
        <li className="text-sm sm:text-base text-foreground/90">Design an agent orchestration platform &mdash; schedule, execute, and monitor thousands of autonomous AI agent tasks with retries, priorities, and tenant isolation.</li>
        <li className="text-sm sm:text-base text-foreground/90">Design a deployment pipeline for a platform where each user gets their own preview environment &mdash; manage pods, domains, certificates, and rollback.</li>
        <li className="text-sm sm:text-base text-foreground/90">Design an experimentation framework for A/B testing AI agent configurations &mdash; traffic splitting, metric collection, statistical significance, guardrails.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">For each:</span> draw the architecture diagram first, walk through failure modes, then discuss what changes at 10x and 100x scale.</li>
      </ul>
    </SectionWrapper>
  );
}

/* =================== LLD =================== */
function LLDSection() {
  return (
    <SectionWrapper id="lld-round" icon={Layers} number="05" title="Low Level Design (LLD) Round" badge="55 - 65 min">
      <p className="text-sm sm:text-base leading-7 text-muted-foreground mb-5 max-w-[68ch]">
        You'll design the internals of a specific component &mdash; data models, API contracts, concurrency control, storage layouts, and migration strategies. Think of LLD as zooming into one box from an HLD diagram and making every interface concrete.
      </p>

      <p className="subsection-title">Core Concepts to Know</p>
      <ul className="guide-list">
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Database schema design:</span> Normalization, indexing strategies, composite keys, foreign key constraints. Know how to design a schema that handles concurrent writes and efficient reads.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">API contract design:</span> REST and gRPC interface signatures. Input validation, error codes, pagination, idempotency keys.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Concurrency control:</span> Optimistic concurrency (version guards, CAS), pessimistic locking, and when each is appropriate. Understand thundering herd and how to mitigate it.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Event sourcing and CQRS:</span> When to store events vs snapshots, how to rebuild state, compaction strategies.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Migration strategies:</span> Online schema migration (expand-contract pattern), dual-writes, backfill strategies. How to migrate without downtime.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Storage efficiency:</span> When full snapshots become expensive and how to use deltas, checkpoints, and compaction.</li>
      </ul>

      <p className="subsection-title">What We Evaluate</p>
      <div className="table-wrapper mb-5">
        <table className="eval-table">
          <thead>
            <tr>
              <th>Criteria</th>
              <th>What "Good" Looks Like</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Data Model</td>
              <td>Concrete schemas with column types, indexes, and constraints. Not hand-wavy "we'll store it in a database"</td>
            </tr>
            <tr>
              <td>API/Interface Design</td>
              <td>Clear function signatures with typed parameters, return types, and error cases. Idempotency where needed</td>
            </tr>
            <tr>
              <td>Concurrency Safety</td>
              <td>You identify where concurrent access happens and design explicit guards &mdash; version checks, atomic operations, conflict resolution strategies</td>
            </tr>
            <tr>
              <td>Storage Efficiency</td>
              <td>You reason about storage growth and propose strategies to manage it &mdash; compaction, TTLs, archival</td>
            </tr>
            <tr>
              <td>Edge Cases</td>
              <td>You proactively identify interactions between features &mdash; what happens when rollback meets compaction? When migration meets concurrent writes?</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="subsection-title">Common Mistakes to Avoid</p>
      <ul className="guide-list">
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Staying abstract.</span> "We'll have a table for messages" is not LLD. Show the DDL. Show the columns. Show the indexes.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Ignoring concurrent access.</span> If two clients can modify the same resource, you need a conflict strategy. Not "we'll add a lock" &mdash; what kind of lock? What happens on conflict?</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Forgetting side effects.</span> If an operation triggers external side effects (charges, notifications, API calls), what happens when you undo the operation? You can't un-send an email.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">No migration story.</span> If the schema needs to change in 6 months, how do you handle it? Downtime migration is not acceptable.</li>
      </ul>

      <p className="subsection-title">Practice Strategy</p>
      <ul className="guide-list">
        <li className="text-sm sm:text-base text-foreground/90">Design a versioned document store with optimistic concurrency, rollback, and compaction. Define every interface signature and the database schema.</li>
        <li className="text-sm sm:text-base text-foreground/90">Design a chat message storage system &mdash; schema for messages, conversations, branches, reactions, and tool calls. Handle context window retrieval under a token budget.</li>
        <li className="text-sm sm:text-base text-foreground/90">Design an LLM request routing layer &mdash; concrete interfaces for model registry, rate limiter, health tracker, and request pipeline. Define the data model for each.</li>
        <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">For each:</span> write the actual interface signatures (not pseudocode), define the database schema with indexes, and walk through concurrent access scenarios.</li>
      </ul>
    </SectionWrapper>
  );
}

/* =================== GENERAL TIPS =================== */
function GeneralTipsSection() {
  return (
    <SectionWrapper id="general-tips" icon={Lightbulb} number="06" title="General Tips (All Rounds)">
      {/* Think Aloud */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare className="w-4 h-4 text-muted-foreground" />
          <p className="subsection-title" style={{ margin: 0, border: "none", padding: 0 }}>Think Aloud</p>
        </div>
        <p className="text-sm sm:text-base leading-7 text-muted-foreground mb-3 max-w-[68ch]">
          The interviewer wants to understand your reasoning, not just your answer. Narrate your thought process:
        </p>
        <div className="space-y-2 ml-1">
          <p className="text-sm text-foreground/80 italic">"I'm choosing X over Y because..."</p>
          <p className="text-sm text-foreground/80 italic">"The tradeoff here is..."</p>
          <p className="text-sm text-foreground/80 italic">"I'm worried about this edge case..."</p>
          <p className="text-sm text-foreground/80 italic">"At scale, this would break because..."</p>
        </div>
        <p className="text-sm sm:text-base leading-7 text-muted-foreground mt-3 max-w-[68ch]">
          <span className="font-medium text-foreground">Silence is a missed signal.</span> If you're thinking, say what you're thinking about.
        </p>
      </div>

      <Separator className="my-6" />

      {/* Ask Clarifying Questions */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <HelpCircle className="w-4 h-4 text-muted-foreground" />
          <p className="subsection-title" style={{ margin: 0, border: "none", padding: 0 }}>Ask Clarifying Questions</p>
        </div>
        <p className="text-sm sm:text-base leading-7 text-muted-foreground mb-3 max-w-[68ch]">
          Good questions show depth of understanding:
        </p>
        <div className="space-y-2 mb-3">
          <div className="example-good">
            <p className="text-sm text-foreground/80"><span className="font-medium text-foreground">Good:</span> "When you say concurrent, how many concurrent writers are we expecting? 10 or 10,000?"</p>
          </div>
          <div className="example-good">
            <p className="text-sm text-foreground/80"><span className="font-medium text-foreground">Good:</span> "Should the system prioritize consistency or availability when the network partitions?"</p>
          </div>
          <div className="example-good">
            <p className="text-sm text-foreground/80"><span className="font-medium text-foreground">Good:</span> "Is this a single-region deployment or multi-region?"</p>
          </div>
          <div className="example-bad">
            <p className="text-sm text-foreground/80"><span className="font-medium" style={{ color: "hsl(0 60% 50%)" }}>Bad:</span> "Can I use any language?"</p>
          </div>
          <div className="example-bad">
            <p className="text-sm text-foreground/80"><span className="font-medium" style={{ color: "hsl(0 60% 50%)" }}>Bad:</span> "How much time do I have?"</p>
          </div>
        </div>
      </div>

      <Separator className="my-6" />

      {/* Progressive Disclosure */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Target className="w-4 h-4 text-muted-foreground" />
          <p className="subsection-title" style={{ margin: 0, border: "none", padding: 0 }}>Progressive Disclosure = Progressive Depth</p>
        </div>
        <p className="text-sm sm:text-base leading-7 text-muted-foreground mb-3 max-w-[68ch]">
          Phase 1 tests fundamentals. Phase 2 tests depth. Phase 3 tests ceiling.
        </p>
        <ul className="guide-list">
          <li className="text-sm sm:text-base text-foreground/90">Don't try to solve Phase 2 during Phase 1 &mdash; focus on a clean, working baseline first.</li>
          <li className="text-sm sm:text-base text-foreground/90">When Phase 2 is revealed, take a moment to understand how new requirements interact with your existing design. Sometimes they change the architecture.</li>
          <li className="text-sm sm:text-base text-foreground/90">Phase 3 follow-ups are designed to push you. It's okay to not know everything &mdash; but show how you reason under uncertainty.</li>
        </ul>
      </div>

      <Separator className="my-6" />

      {/* Hints */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Brain className="w-4 h-4 text-muted-foreground" />
          <p className="subsection-title" style={{ margin: 0, border: "none", padding: 0 }}>Hints Are Not Penalties</p>
        </div>
        <p className="text-sm sm:text-base leading-7 text-muted-foreground mb-3 max-w-[68ch]">
          If the interviewer offers a hint, take it. Using a hint well is a positive signal:
        </p>
        <ul className="guide-list">
          <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Strong signal:</span> You take the hint, build on it, and extend it with your own reasoning.</li>
          <li className="text-sm sm:text-base text-foreground/90"><span className="font-medium">Weak signal:</span> You take the hint and implement it mechanically without understanding why.</li>
        </ul>
        <p className="text-sm sm:text-base leading-7 text-muted-foreground mt-3 max-w-[68ch]">
          The interviewer is not trying to trick you &mdash; they're trying to see the best version of your thinking.
        </p>
      </div>

      <Separator className="my-6" />

      {/* Time Management */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-muted-foreground" />
          <p className="subsection-title" style={{ margin: 0, border: "none", padding: 0 }}>Time Management</p>
        </div>
        <p className="text-sm sm:text-base leading-7 text-muted-foreground mb-3 max-w-[68ch]">
          The interviewer manages time, but you should be aware of pacing:
        </p>
        <ul className="guide-list">
          <li className="text-sm sm:text-base text-foreground/90">Don't spend 25 minutes on clarifying questions.</li>
          <li className="text-sm sm:text-base text-foreground/90">Don't get stuck on one part for 10+ minutes without saying something.</li>
          <li className="text-sm sm:text-base text-foreground/90">If you're stuck, say so: "I'm stuck on X. Let me try approaching it from Y instead."</li>
        </ul>
      </div>
    </SectionWrapper>
  );
}

/* =================== WHAT WE VALUE =================== */
function ValuesSection() {
  const values = [
    { we: "Depth over breadth \u2014 going deep on one system beats knowing 10 superficially", not: "Name-dropping technologies without understanding them" },
    { we: "Production thinking \u2014 \"what breaks at 3am?\"", not: "Textbook answers with no operational awareness" },
    { we: "Tradeoff reasoning \u2014 \"I chose X because Y, despite Z\"", not: "\"Best practice\" without context" },
    { we: "Ownership instinct \u2014 \"I'd want to own this and be on-call for it\"", not: "\"Someone else would handle the ops part\"" },
    { we: "Intellectual honesty \u2014 \"I don't know, but here's how I'd figure it out\"", not: "Bluffing through topics you don't understand" },
    { we: "Building for failure \u2014 systems that degrade gracefully", not: "Happy-path-only designs" }
  ];

  return (
    <SectionWrapper id="what-we-value" icon={Heart} number="07" title="What Emergent Values">
      <div className="table-wrapper">
        <table className="values-table">
          <thead>
            <tr>
              <th>We Value</th>
              <th>We Don't Value</th>
            </tr>
          </thead>
          <tbody>
            {values.map((row, i) => (
              <tr key={i}>
                <td className="text-sm text-foreground/90">{row.we}</td>
                <td className="text-sm text-muted-foreground">{row.not}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionWrapper>
  );
}

/* =================== DAY-OF CHECKLIST =================== */
function ChecklistSection() {
  const items = [
    "Have your coding environment ready (IDE, compiler/interpreter, no setup surprises)",
    "Test your screen-sharing / whiteboard tool if the interview is remote",
    "Have a way to draw diagrams quickly (Excalidraw, Miro, pen+paper on camera)",
    "Be ready to code in your strongest language \u2014 don't pick one you're rusty in",
    "Read this guide one more time"
  ];

  return (
    <SectionWrapper id="day-of-checklist" icon={ClipboardCheck} number="08" title="Day-of Checklist">
      <div className="space-y-0">
        {items.map((item, i) => (
          <div key={i} className="checklist-item">
            <div className="checklist-icon" />
            <p className="text-sm sm:text-base text-foreground/90 leading-6">{item}</p>
          </div>
        ))}
      </div>
    </SectionWrapper>
  );
}

/* =================== FOOTER =================== */
function Footer() {
  return (
    <footer data-testid="site-footer" className="border-t" style={{ borderColor: "hsl(0 0% 90%)" }}>
      <div className="mx-auto w-full max-w-[72rem] px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-xs sm:text-sm" style={{ color: "rgb(102, 102, 102)" }}>
          Candidate Interview Prep Guide by{" "}
          <a href="https://emergent.sh" target="_blank" rel="noopener noreferrer" className="font-medium underline underline-offset-4 hover:opacity-70" style={{ color: "rgb(0, 0, 0)", textDecorationColor: "hsl(0 0% 82%)" }}>Emergent</a>
        </p>
        <p className="text-xs" style={{ color: "rgb(102, 102, 102)" }}>
          Come ready to show us how you think.
        </p>
      </div>
    </footer>
  );
}

/* =================== MAIN APP =================== */
function App() {
  const [activeId, setActiveId] = useState(NAV_ITEMS[0].id);

  const handleIntersection = useCallback((entries) => {
    const visible = entries.filter((e) => e.isIntersecting);
    if (visible.length > 0) {
      const top = visible.reduce((a, b) =>
        a.boundingClientRect.top < b.boundingClientRect.top ? a : b
      );
      setActiveId(top.target.id);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: "-10% 0px -50% 0px",
      threshold: 0
    });

    NAV_ITEMS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [handleIntersection]);

  return (
    <div className="min-h-screen bg-background">
      {/* Skip link */}
      <a href="#about-emergent" className="skip-link" data-testid="skip-to-content-link">
        Skip to content
      </a>

      {/* Hero */}
      <HeroSection />

      {/* Sticky Nav */}
      <StickyNav activeId={activeId} />

      {/* Sections */}
      <main
        id="main-content"
        className="mx-auto w-full max-w-[72rem] px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-14"
      >
        <div className="space-y-6 sm:space-y-8 stagger-children">
          <AboutSection />
          <FormatSection />
          <MachineCodingSection />
          <HLDSection />
          <LLDSection />
          <GeneralTipsSection />
          <ValuesSection />
          <ChecklistSection />
        </div>
      </main>

      {/* Closing message */}
      <div className="mx-auto w-full max-w-[72rem] px-4 sm:px-6 lg:px-8 pb-10">
        <Separator className="mb-8" style={{ backgroundColor: "hsl(0 0% 90%)" }} />
        <div className="text-center">
          <p className="text-sm sm:text-base leading-7 max-w-[56ch] mx-auto" style={{ color: "rgb(69, 69, 84)" }}>
            The best interviews feel like pair-programming with a colleague. We're not looking for trick-answer recall. We're looking for engineers who <span className="font-semibold" style={{ color: "rgb(0, 0, 0)" }}>think deeply about systems</span>, <span className="font-semibold" style={{ color: "rgb(0, 0, 0)" }}>communicate clearly about tradeoffs</span>, and <span className="font-semibold" style={{ color: "rgb(0, 0, 0)" }}>build things that work when things go wrong</span>.
          </p>
          <p className="mt-5 text-lg sm:text-xl font-semibold" style={{ fontFamily: "var(--font-heading)", letterSpacing: "-0.02em", color: "rgb(0, 0, 0)" }}>
            Come ready to show us how you think.
          </p>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default App;
